import React, { useEffect, useState, useRef } from 'react';
import { Box, Paper, Typography, IconButton, Button, TextField, Popover } from '@mui/material';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import RemoveIcon from '@mui/icons-material/Remove';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import { getNewsHeadersApi, createNewsHeaderApi, updateNewsHeaderApi } from '../../api';
import ConfirmModal from '../common/ConfirmModal';

const HEADER_COLORS = [
  { name: '회색', code: '#F0EFED' },
  { name: '빨강', code: '#FCE9E7' },
  { name: '주황', code: '#FBEBDE' },
  { name: '노랑', code: '#F9F3DC' },
  { name: '초록', code: '#E8F1EC' },
  { name: '파랑', code: '#E5F2FC' },
  { name: '보라', code: '#F3EBF9' },
  { name: '갈색', code: '#F5EDE9' },
  { name: '핑크', code: '#FAE9F1' },
];

const NewsHeaderEditModal = ({
  open,
  headers = [],
  onChangeHeaderName,
  onRemoveHeader,
  onAddHeader,
  onSubmit,
  onClose,
}) => {
  const [items, setItems] = useState(headers);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false); 
  const [initialItems, setInitialItems] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedColorIndex, setSelectedColorIndex] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const dragIndexRef = useRef(null);
  const [nameErrorIndices, setNameErrorIndices] = useState([]);

  useEffect(() => {
    if (!open) return;

    const fetchHeaders = async () => {
      try {
        setError(null);
        const res = await getNewsHeadersApi();
        const list = Array.isArray(res?.data) ? res.data : res;
        const normalized = (list || []).map((item) => ({
          headerId: item.headerId ?? item.header_id,
          name: item.name,
          color: item.color || HEADER_COLORS[0].code,
          useScope: (item.useScope || item.use_scope || 'ALL') || 'ALL',
          isActive:
            typeof item.isActive === 'boolean'
              ? item.isActive
              : item.is_active !== undefined
                ? !!item.is_active
                : true,
          isDeleted: false,
        }));
        setItems(normalized);
        setInitialItems(normalized);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load news headers:', e);
        setError(e.message || '말머리 목록을 불러오지 못했습니다.');
      } finally {
        // no-op
      }
    };

    fetchHeaders();
  }, [open]);

  if (!open) return null;

  const handleChangeName = (index, value) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, name: value } : item)),
    );
    if (onChangeHeaderName) {
      onChangeHeaderName(index, value);
    }
    setNameErrorIndices((prev) => prev.filter((i) => i !== index));
  };

  const handleRemove = (index) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, isDeleted: !item.isDeleted } : item)),
    );
  };

  const handleAdd = () => {
    setItems((prev) => [
      ...prev,
      {
        headerId: Date.now(),
        name: '',
        color: HEADER_COLORS[0].code,
        useScope: 'ALL',
        isActive: true,
        isDeleted: false,
      },
    ]);
  };

  const processSave = async () => {
    try {
      setSaving(true);

      const current = items.map((item, index) => ({
        ...item,
        sortOrder: index + 1,
      }));

      const initialById = new Map(
        initialItems
          .filter((h) => h.headerId)
          .map((h) => [h.headerId, h]),
      );

      // eslint-disable-next-line no-restricted-syntax
      for (const item of current) {
        const isNew = !item.headerId || !initialById.has(item.headerId);

        if (item.isDeleted) {
          if (!isNew) {
            // eslint-disable-next-line no-await-in-loop
            await updateNewsHeaderApi(item.headerId, {
              isActive: 0,
            });
          }
          continue;
        }

        if (isNew) {
          // eslint-disable-next-line no-await-in-loop
          await createNewsHeaderApi({
            name: item.name,
            sortOrder: item.sortOrder,
            isActive: 1,
            color: item.color,
            useScope: item.useScope || 'ALL',
          });
        } else {
          // eslint-disable-next-line no-await-in-loop
          await updateNewsHeaderApi(item.headerId, {
            name: item.name,
            sortOrder: item.sortOrder,
            isActive: 1,
            color: item.color,
            useScope: item.useScope || 'ALL',
          });
        }
      }

      const res = await getNewsHeadersApi();
      const list = Array.isArray(res?.data) ? res.data : res;
      const normalized = (list || []).map((item) => ({
        headerId: item.headerId ?? item.header_id,
        name: item.name,
        color: item.color || HEADER_COLORS[0].code,
        useScope: (item.useScope || item.use_scope || 'ALL') || 'ALL',
        isActive:
          typeof item.isActive === 'boolean'
            ? item.isActive
            : item.is_active !== undefined
              ? !!item.is_active
              : true,
        isDeleted: false,
      }));
      setItems(normalized);
      setInitialItems(normalized);

      if (onSubmit) {
        onSubmit();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to save news headers:', e);
      setError(e.message || '말머리 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = () => {
    const nameMap = new Map();
    items.forEach((item, idx) => {
      const key = (item.name || '').trim();
      if (!key) return;
      const list = nameMap.get(key) || [];
      list.push(idx);
      nameMap.set(key, list);
    });

    const dupIdx = [];
    nameMap.forEach((list) => {
      if (list.length > 1) {
        dupIdx.push(...list);
      }
    });

    if (dupIdx.length > 0) {
      setNameErrorIndices(dupIdx);
      setError('중복된 말머리 이름이 있습니다. 각 말머리는 서로 다른 이름이어야 합니다.');
      return;
    }

    setNameErrorIndices([]);
    
    setConfirmOpen(true);
  };

  const handleConfirmAction = () => {
    setConfirmOpen(false);
    processSave();
  };

  const handleColorClick = (event, index) => {
    setAnchorEl(event.currentTarget);
    setSelectedColorIndex(index);
  };

  const handleColorClose = () => {
    setAnchorEl(null);
    setSelectedColorIndex(null);
  };

  const handleColorSelect = (colorCode) => {
    if (selectedColorIndex !== null) {
      setItems((prev) =>
        prev.map((item, i) =>
          i === selectedColorIndex ? { ...item, color: colorCode } : item
        )
      );
    }
    handleColorClose();
  };

  const openPopover = Boolean(anchorEl);

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
    if (dragIndex === null || dragIndex === index) return;

    setItems((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(dragIndex, 1);
      updated.splice(index, 0, moved);
      return updated;
    });

    setDragIndex(index);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  const handleTouchStart = (index) => {
    setDragIndex(index);
    dragIndexRef.current = index;
  };

  const handleTouchMove = (e) => {
    if (e.cancelable) e.preventDefault();
    
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    
    if (!element) return;
    
    const row = element.closest('[data-index]');
    if (!row) return;
    
    const newIndex = parseInt(row.getAttribute('data-index'), 10);
    const currentIndex = dragIndexRef.current;
    
    if (
      newIndex !== null &&
      !isNaN(newIndex) &&
      currentIndex !== null &&
      currentIndex !== newIndex
    ) {
      setItems((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(currentIndex, 1);
        updated.splice(newIndex, 0, moved);
        return updated;
      });
      
      setDragIndex(newIndex);
      dragIndexRef.current = newIndex;
    }
  };

  const handleTouchEnd = () => {
    setDragIndex(null);
    dragIndexRef.current = null;
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'rgba(0,0,0,0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
      }}
      onClick={onClose}
    >
      <Paper
        elevation={3}
        sx={{
          width: 360,
          maxWidth: '90%',
          p: 3,
          pt: 3.5,
          pb: 3.5,
          textAlign: 'center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, mb: 2 }}
        >
          말머리 수정
        </Typography>

        <Box sx={{ mb: 2 }}>
          {!error && items.length === 0 && (
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              등록된 말머리가 없습니다.
            </Typography>
          )}

          {items.map((header, index) => {
            const isDragging = dragIndex === index;
            return (
              <Box
                key={header.headerId ?? header.id ?? index}
                data-index={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: header.isDeleted 
                    ? '1px solid #ef9a9a' 
                    : isDragging 
                      ? '2px solid #0d47a1'
                      : '1px solid #e0e0e0',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.75,
                  mb: 1,
                  bgcolor: header.isDeleted 
                    ? '#ffebee' 
                    : isDragging 
                      ? '#e3f2fd'
                      : '#ffffff',
                  opacity: header.isDeleted ? 0.8 : isDragging ? 0.9 : 1,
                  transform: isDragging ? 'scale(1.02)' : 'scale(1)',
                  boxShadow: isDragging ? '0 4px 8px rgba(0,0,0,0.1)' : 'none',
                  transition: 'transform 0.2s, box-shadow 0.2s, background-color 0.2s',
                  position: 'relative',
                  zIndex: isDragging ? 1 : 'auto',
                }}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                onDrop={handleDragEnd}
              >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mr: 1.5,
                  touchAction: 'none', 
                }}
                onTouchStart={() => handleTouchStart(index)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <DragHandleIcon sx={{ fontSize: 20, color: '#333', cursor: 'grab' }} />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 1.5 }}>
                <input
                  type="checkbox"
                  checked={String(header.useScope || 'ALL').toUpperCase() === 'ALL'}
                  onChange={(e) =>
                    setItems((prev) =>
                      prev.map((it, i) =>
                        i === index ? { ...it, useScope: e.target.checked ? 'ALL' : 'ADMIN' } : it,
                      ),
                    )
                  }
                  disabled={header.isDeleted}
                  style={{ width: 16, height: 16, cursor: header.isDeleted ? 'default' : 'pointer' }}
                />
                <Box
                  onClick={(e) => !header.isDeleted && handleColorClick(e, index)}
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    bgcolor: header.color || HEADER_COLORS[0].code, 
                    cursor: header.isDeleted ? 'default' : 'pointer',
                    border: '1px solid #ddd',
                    flexShrink: 0,
                    '&:hover': {
                      opacity: header.isDeleted ? 1 : 0.8,
                    },
                  }}
                />
              </Box>

              <TextField
                value={header.name}
                onChange={(e) => handleChangeName(index, e.target.value)}
                variant="standard"
                placeholder="말머리 입력"
                error={nameErrorIndices.includes(index)}
                disabled={header.isDeleted}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: 15,
                    fontWeight: 600,
                    px: 0,
                    textDecoration: 'none',
                    color: header.isDeleted ? 'text.disabled' : 'inherit',
                  },
                }}
                sx={{
                  flex: 1,
                  mr: 0.5,
                  '& .MuiInputBase-root': {
                    borderRadius: 1,
                    border: nameErrorIndices.includes(index)
                      ? '1px solid #f44336'
                      : '1px solid transparent',
                    px: 0.5,
                  },
                }}
              />

              <IconButton
                size="small"
                onClick={() => handleRemove(index)}
              >
                {header.isDeleted ? (
                  <AddIcon sx={{ fontSize: 20, color: '#333' }} />
                ) : (
                  <RemoveIcon sx={{ fontSize: 20, color: '#333' }} />
                )}
              </IconButton>
            </Box>
          );
          })}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <AddCircleOutlineIcon sx={{ fontSize: 30, color: '#999', cursor: 'pointer' }} onClick={handleAdd} />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            sx={{
              py: 1.2,
              fontSize: 16,
              fontWeight: 600,
              borderColor: '#d0d0d0',
              color: '#333',
              '&:hover': {
                borderColor: '#b0b0b0',
                bgcolor: '#f8f8f8',
              },
            }}
          >
            취소
          </Button>
          <Button
            variant="contained"
            fullWidth
            sx={{
              bgcolor: '#0d47a1',
              fontWeight: 600,
              py: 1.2,
              fontSize: 16,
              '&:hover': {
                bgcolor: '#0b3a81',
              },
            }}
            disabled={saving}
            onClick={handleSubmit}
          >
            {saving ? '저장 중...' : '수정'}
          </Button>
        </Box>
      </Paper>

      <Popover
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleColorClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            p: 2,
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 1.5,
          }}
        >
          {HEADER_COLORS.map((color) => (
            <Box
              key={color.code}
              onClick={(e) => {
                e.stopPropagation();
                handleColorSelect(color.code);
              }}
              title={color.name}
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: color.code,
                cursor: 'pointer',
                border: '1px solid #eee',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.2)',
                  borderColor: '#aaa',
                },
              }}
            />
          ))}
        </Box>
      </Popover>
      <ConfirmModal
        open={confirmOpen}
        message="말머리를 수정하시겠습니까?"
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
};

export default NewsHeaderEditModal;
